<template>
  <div class="resultados-container">
    <Card class="main-card">
      <template #header>
        <div class="header-section">
          <h2 class="page-title">
            <i class="pi pi-search"></i>
            RESULTADOS DA BUSCA
          </h2>

          <!-- Observação 1 - Alert sobre numeração -->
          <div class="info-alert">
            <div class="alert-icon">
              <i class="pi pi-info-circle"></i>
            </div>
            <div class="alert-content">
              <h4 class="alert-title">Atenção sobre a numeração</h4>
              <p>
                Alguns decisórios podem apresentar em seu conteúdo uma 
                numeração de processo diferente do listado no resultado. 
                Isso pode ocorrer devido à digitalização de processos 
                físicos que, ao tornarem-se eletrônicos, recebem nova 
                numeração. <strong>Entre em contato conosco em caso de dúvidas.</strong>
              </p>
            </div>
          </div>

          <!-- Observação 2 - Alert sobre periodicidade das sessões -->
          <div class="info-alert session-alert">
            <div class="alert-icon session-icon">
              <i class="pi pi-calendar"></i>
            </div>
            <div class="alert-content">
              <h4 class="alert-title">Periodicidade das Sessões</h4>
              <p>
                As sessões seguem periodicidade <strong>semanal</strong> (Tribunal Pleno) 
                e <strong>mensal</strong> (Câmaras), podendo haver alterações em função de 
                períodos de recesso no TCE/AM, feriados e outros eventos.
              </p>
              <p class="session-link">
                <i class="pi pi-external-link"></i>
                Consulte as sessões realizadas e futuras com pauta definida em: 
                <a href="https://dec.tceam.tc.br/consulta-sessao-publica" target="_blank" rel="noopener noreferrer">
                  dec.tceam.tc.br/consulta-sessao-publica
                </a>
              </p>
            </div>
          </div>

          <div class="controls-bar">
            <div class="results-count" v-if="decisorios.length > 0">
              <i class="pi pi-file"></i>
              <span>{{ decisorios.length }} resultado(s) encontrado(s)</span>
            </div>
            <div class="sort-control">
              <label for="sortDropdown">
                <i class="pi pi-sort-alt"></i>
                Ordenar:
              </label>
              <Dropdown
                id="sortDropdown"
                v-model="sortKey"
                :options="sortOptions"
                optionLabel="label"
                placeholder="Selecione..."
                @change="onSortChange($event)"
                class="sort-dropdown"
              />
            </div>
          </div>
        </div>
      </template>

      <template #content>
        <DataView
          :value="decisorios"
          :sortOrder="sortOrder"
          :sortField="sortField"
          paginatorPosition="both"
          :paginator="true"
          :rows="20"
          class="decisorios-dataview"
          ref="dv"
        >
          <template #list="slotProps">
            <div class="decisorio-item">
              <Panel class="decisorio-panel">
                <template #header>
                  <!-- LINHA 1: Informações Principais -->
                  <div class="panel-header">
                    <div class="header-row header-row-primary">
                      <div class="info-item">
                        <div class="info-header">
                          <i class="pi pi-folder"></i>
                          <span class="info-title">Processo</span>
                        </div>
                        <div class="info-data">
                          {{ slotProps.data.numeroProcesso }}/{{ slotProps.data.anoProcesso }}
                        </div>
                      </div>

                      <div class="info-item">
                        <div class="info-header">
                          <i class="pi pi-tag"></i>
                          <span class="info-title">Natureza</span>
                        </div>
                        <div class="info-data">
                          {{ slotProps.data.natureza }}
                        </div>
                      </div>

                      <div class="info-item" v-if="slotProps.data.orgaoJulgador">
                        <div class="info-header">
                          <i class="pi pi-building"></i>
                          <span class="info-title">Órgão Julgador</span>
                        </div>
                        <div class="info-data">
                          {{ slotProps.data.orgaoJulgador }}
                        </div>
                      </div>
                    </div>

                    <!-- LINHA 2: Informações Secundárias + Ação -->
                    <div class="header-row header-row-secondary">
                      <div class="info-item" v-if="slotProps.data.relatorVotoDecisao">
                        <div class="info-header">
                          <i class="pi pi-user"></i>
                          <span class="info-title">Relator</span>
                        </div>
                        <div class="info-data">
                          {{ slotProps.data.relatorVotoDecisao }}
                        </div>
                      </div>

                      <div class="info-item" v-if="slotProps.data.numeroDecisorio">
                        <div class="info-header">
                          <i class="pi pi-file-edit"></i>
                          <span class="info-title">Decisório</span>
                        </div>
                        <div class="info-data">
                          {{ slotProps.data.numeroDecisorio }}/{{ slotProps.data.anoDecisorio }}
                        </div>
                      </div>

                      <div class="info-item" v-if="slotProps.data.dataSessao">
                        <div class="info-header">
                          <i class="pi pi-calendar"></i>
                          <span class="info-title">Data da Sessão</span>
                        </div>
                        <div class="info-data">
                          {{ formatarData(slotProps.data.dataSessao) }}
                        </div>
                      </div>

                      <div class="info-item info-item-action">
                        <Button
                          icon="pi pi-download"
                          label="Visualizar PDF"
                          @click="download(slotProps.data.caminhoArquivo)"
                          class="download-btn"
                        />
                      </div>
                    </div>
                  </div>
                </template>

                <div class="panel-body">
                  <div class="detail-row">
                    <strong class="detail-label">
                      <i class="pi pi-bookmark"></i> Objeto:
                    </strong>
                    <span class="detail-text">{{ slotProps.data.objeto }}</span>
                  </div>

                  <div class="detail-row">
                    <strong class="detail-label">
                      <i class="pi pi-sitemap"></i> Órgão:
                    </strong>
                    <span class="detail-text">{{ slotProps.data.orgao }}</span>
                  </div>

                  <div class="detail-row">
                    <strong class="detail-label">
                      <i class="pi pi-users"></i> Partes:
                    </strong>
                    <span class="detail-text">{{ slotProps.data.partes }}</span>
                  </div>

                  <div class="detail-description" v-if="slotProps.data.descricao">
                    <strong class="detail-label">
                      <i class="pi pi-align-left"></i> Descrição:
                    </strong>
                    <div v-html="slotProps.data.descricao" class="description-content" />
                  </div>
                </div>
              </Panel>
            </div>
          </template>

          <template #empty>
            <div class="empty-state">
              <div v-if="decisorios.length == 0">
                <i class="pi pi-search empty-icon"></i>
                <h3>Nenhum resultado encontrado</h3>
                <p>
                  Não foi encontrado nenhum resultado para os critérios de busca
                  estabelecidos. Verifique se é possível ajustar os valores para
                  tornar a pesquisa menos restritiva e tente novamente.
                </p>
              </div>
              <div v-else>
                <i class="pi pi-search empty-icon"></i>
                <p>Realize a pesquisa para visualizar os resultados</p>
              </div>
            </div>
          </template>
        </DataView>

        <!-- Observação 3 - Warning Card sobre limite -->
        <div class="limit-warning-card">
          <div class="warning-icon">
            <i class="pi pi-exclamation-triangle"></i>
          </div>
          <div class="warning-content">
            <strong>Limite de resultados:</strong>
            Esta busca está limitada aos primeiros <strong>1.000 (mil) registros</strong> 
            e abrange decisórios a partir de <strong>2016</strong>.
          </div>
        </div>

        <Loading />
        <Toast />
      </template>
    </Card>
  </div>
</template>

<script>
import Loading from "../pages/Loading.vue";

export default {
  data() {
    return {
      loading: false,
      sortKey: null,
      sortOrder: null,
      sortField: null,
      sortOptions: [
        { label: "Sessão mais recente", value: "!dataSessao" },
        { label: "Sessão mais antiga", value: "dataSessao" },
        { label: "Relator", value: "relatorVotoDecisao" },
        { label: "Natureza", value: "natureza" },
        { label: "Decisório", value: "numeroDecisorio" }
      ],
    };
  },

  methods: {
    formatarData(dataCrua) {
      var strData = "" + dataCrua;
      return (
        strData.substring(6) +
        "/" +
        strData.substring(4, 6) +
        "/" +
        strData.substring(0, 4)
      );
    },

    download(url) {
      window.open(url);
    },

    onSortChange(event) {
      const value = event.value.value;
      const sortValue = event.value;

      if (value.indexOf("!") === 0) {
        this.sortOrder = -1;
        this.sortField = value.substring(1, value.length);
        this.sortKey = sortValue;
      } else {
        this.sortOrder = 1;
        this.sortField = value;
        this.sortKey = sortValue;
      }
    },
  },

  components: {
    Loading,
  },

  props: {
    decisorios: {
      type: Array,
      required: true,
    },
  },
};
</script>

<style scoped>
/* ============================================
   CONTAINER PRINCIPAL
   ============================================ */
.resultados-container {
  padding: 2rem 1rem;
  max-width: 1400px;
  margin: 0 auto;
}

.main-card {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  overflow: hidden;
}

/* ============================================
   HEADER SECTION
   ============================================ */
.header-section {
  padding: 2rem;
  background: #5a8bb5;
  color: #ffffff;
}

.page-title {
  font-size: 1.75rem;
  font-weight: 600;
  margin: 0 0 1.5rem 0;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  letter-spacing: 0.5px;
  color: #ffffff;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
}

.page-title i {
  font-size: 1.5rem;
}

/* ============================================
   ALERTS INFORMATIVOS (Observações 1 e 2)
   ============================================ */
.info-alert {
  background: rgba(248, 204, 70, 0.15);
  border-left: 4px solid #f8cc46;
  border-radius: 8px;
  padding: 1.25rem;
  margin-bottom: 1rem;
  display: flex;
  gap: 1rem;
  backdrop-filter: blur(10px);
  animation: slideDown 0.4s ease-out;
}

.info-alert:last-of-type {
  margin-bottom: 1.5rem;
}

.session-alert {
  background: rgba(124, 179, 227, 0.15);
  border-left: 4px solid #7cb3e3;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.alert-icon {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  background: #f8cc46;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #335e82;
  font-size: 1.25rem;
}

.session-icon {
  background: #7cb3e3;
  color: #ffffff;
}

.alert-content {
  flex: 1;
}

.alert-title {
  margin: 0 0 0.5rem 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #ffffff;
}

.alert-content p {
  margin: 0 0 0.5rem 0;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.95);
  font-size: 0.95rem;
}

.alert-content p:last-child {
  margin-bottom: 0;
}

.alert-content strong {
  color: #f8cc46;
  font-weight: 600;
}

.session-alert .alert-content strong {
  color: #ffffff;
  background: rgba(124, 179, 227, 0.3);
  padding: 0.125rem 0.375rem;
  border-radius: 3px;
}

.session-link {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin-top: 0.75rem !important;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
}

.session-link i {
  margin-top: 0.25rem;
  flex-shrink: 0;
}

.session-link a {
  color: #ffffff;
  text-decoration: underline;
  font-weight: 600;
  transition: all 0.3s ease;
  word-break: break-all;
}

.session-link a:hover {
  color: #f8cc46;
  text-decoration: none;
}

/* ============================================
   BARRA DE CONTROLES
   ============================================ */
.controls-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  background: rgba(255, 255, 255, 0.1);
  padding: 1rem;
  border-radius: 8px;
}

.results-count {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
  font-size: 1rem;
}

.results-count i {
  font-size: 1.1rem;
  color: #f8cc46;
}

.sort-control {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.sort-control label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
  white-space: nowrap;
}

.sort-dropdown {
  min-width: 200px;
}

/* ============================================
   DATAVIEW
   ============================================ */
.decisorios-dataview {
  padding: 1.5rem 0;
}

.decisorio-item {
  margin-bottom: 1.5rem;
  animation: fadeInUp 0.4s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.decisorio-panel {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  border: 1px solid rgba(51, 94, 130, 0.1);
  transition: all 0.3s ease;
  overflow: hidden;
}

.decisorio-panel:hover {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  transform: translateY(-2px);
}

/* ============================================
   PANEL HEADER - REORGANIZADO E ALINHADO
   ============================================ */
.panel-header {
  background: linear-gradient(to bottom, #f8f9fb 0%, #ffffff 100%);
}

.header-row {
  display: grid;
  gap: 1.5rem;
  padding: 1.25rem 1.5rem;
  align-items: start;
}

/* Primeira linha - 3 colunas iguais */
.header-row-primary {
  grid-template-columns: repeat(3, 1fr);
  border-bottom: 2px solid #e8edf3;
}

/* Segunda linha - 3 colunas de info + 1 coluna de ação */
.header-row-secondary {
  grid-template-columns: 1fr 1fr 1fr auto;
  background: #ffffff;
}

/* Item individual */
.info-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.info-item-action {
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
}

/* Cabeçalho do item (ícone + título) */
.info-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.info-header i {
  color: #5a8bb5;
  font-size: 1rem;
}

.info-title {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #7d89a3;
}

/* Dados do item */
.info-data {
  font-size: 0.95rem;
  font-weight: 500;
  color: #335e82;
  line-height: 1.4;
  padding-left: 1.5rem; /* Alinha com o texto do título */
}

/* Botão de download */
.download-btn {
  background: #335e82 !important;
  border-color: #335e82 !important;
  font-weight: 600;
  padding: 0.75rem 1.5rem !important;
  white-space: nowrap;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(51, 94, 130, 0.2);
}

.download-btn:hover {
  background: #2a4d6a !important;
  border-color: #2a4d6a !important;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(51, 94, 130, 0.3);
}

.download-btn .pi {
  font-size: 1rem;
}

/* ============================================
   PANEL BODY
   ============================================ */
.panel-body {
  padding: 1.5rem;
  background: #ffffff;
}

.detail-row {
  margin-bottom: 1rem;
  display: flex;
  gap: 0.75rem;
  line-height: 1.6;
}

.detail-row:last-child {
  margin-bottom: 0;
}

.detail-label {
  color: #335e82;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: fit-content;
  font-size: 0.95rem;
}

.detail-label i {
  font-size: 0.9rem;
}

.detail-text {
  color: #333;
  font-size: 0.95rem;
}

.detail-description {
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 2px solid #f5f6f8;
}

.description-content {
  margin-top: 0.75rem;
  padding: 1rem;
  background: #f5f6f8;
  border-radius: 8px;
  line-height: 1.7;
  color: #333;
  font-size: 0.95rem;
}

/* ============================================
   WARNING CARD (Observação 3)
   ============================================ */
.limit-warning-card {
  margin-top: 2rem;
  padding: 1.25rem 1.5rem;
  background: linear-gradient(135deg, #fff3cd 0%, #fffaeb 100%);
  border: 2px solid #ffc107;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 1rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    box-shadow: 0 2px 4px rgba(255, 193, 7, 0.2);
  }
  50% {
    box-shadow: 0 4px 12px rgba(255, 193, 7, 0.4);
  }
}

.warning-icon {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  background: #ffc107;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 1.5rem;
  animation: bounce 2s ease-in-out infinite;
}

@keyframes bounce {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-5px);
  }
}

.warning-content {
  color: #856404;
  font-size: 0.95rem;
  line-height: 1.6;
}

.warning-content strong {
  color: #664d03;
  font-weight: 700;
}

/* ============================================
   EMPTY STATE
   ============================================ */
.empty-state {
  text-align: center;
  padding: 4rem 2rem;
  color: #7d89a3;
}

.empty-icon {
  font-size: 4rem;
  color: #335e82;
  opacity: 0.3;
  margin-bottom: 1rem;
}

.empty-state h3 {
  color: #335e82;
  margin: 0 0 0.75rem 0;
  font-weight: 600;
}

.empty-state p {
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
  font-size: 1rem;
}

/* ============================================
   RESPONSIVIDADE
   ============================================ */

/* Tablets */
@media (max-width: 1024px) {
  .header-row-primary {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .header-row-secondary {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .info-item-action {
    grid-column: 1 / -1;
    justify-content: stretch;
  }
  
  .download-btn {
    width: 100%;
  }
}

/* Mobile */
@media (max-width: 768px) {
  .resultados-container {
    padding: 1rem 0.5rem;
  }

  .header-section {
    padding: 1.5rem 1rem;
  }

  .page-title {
    font-size: 1.35rem;
  }

  .controls-bar {
    flex-direction: column;
    align-items: stretch;
  }

  .sort-control {
    flex-direction: column;
    align-items: stretch;
  }

  .sort-dropdown {
    width: 100%;
  }

  .header-row {
    gap: 1rem;
    padding: 1rem;
  }
  
  .header-row-primary,
  .header-row-secondary {
    grid-template-columns: 1fr;
  }
  
  .info-data {
    padding-left: 1.25rem;
    font-size: 0.9rem;
  }

  .info-alert {
    flex-direction: column;
    text-align: center;
  }

  .alert-icon {
    margin: 0 auto;
  }

  .session-link {
    flex-direction: column;
    align-items: center;
  }

  .limit-warning-card {
    flex-direction: column;
    text-align: center;
  }

  .warning-icon {
    margin: 0 auto;
  }
}

@media (max-width: 480px) {
  .page-title {
    font-size: 1.15rem;
    flex-direction: column;
    gap: 0.5rem;
  }

  .header-row {
    padding: 0.75rem;
  }
  
  .info-title {
    font-size: 0.7rem;
  }
  
  .info-data {
    font-size: 0.85rem;
  }

  .detail-row {
    flex-direction: column;
    gap: 0.35rem;
  }

  .session-link a {
    font-size: 0.85rem;
  }
}

/* ============================================
   PRINT STYLES
   ============================================ */
@media print {
  .controls-bar,
  .download-btn,
  .info-alert,
  .limit-warning-card {
    display: none;
  }

  .decisorio-panel {
    break-inside: avoid;
    box-shadow: none;
    border: 1px solid #ccc;
  }
}
</style>

<style>
/* Estilos globais - SEM scoped */
.matched-text {
  font-weight: bold;
  background-color: #f8cc46;
  padding: 0.125rem 0.25rem;
  border-radius: 3px;
  color: #333;
}
</style>